import { t, Trans } from '@lingui/macro';
import { Player } from "@lottiefiles/react-lottie-player";
import { AlertCircle, ArrowLeft, XCircle } from 'lucide-react';
import { useEffect, useState } from "react";
import { useHistory, useParams } from "../../hooks/useRouterCompat";
import { Room } from "../../models/Room";
import { RoomInvite } from "../../models/RoomInvite";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import ProfileImg from "../components/ProfileImg";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardHeader, CardContent, CardFooter } from "../../components/ui/card";
import { Avatar } from "../../components/ui/avatar";
import { Progress } from "../../components/ui/progress";
import { Separator } from "../../components/ui/separator";

const RoomInvitationPage: React.FC = () => {
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [invite, setInvite] = useState<RoomInvite | null>(null);
  const [room, setRoom] = useState<Room>();
  const [isMobileBrowser, setIsMobileBrowser] = useState(false);
  const { invitationId } = useParams<{ invitationId: string }>();
  const roomImage = invite?.roomImageUrl ?? room?.profileImageUrl;
  const roomName = invite?.roomName ?? room?.name;
  const roomDescription = invite?.roomDescription ?? room?.description;
  const [passwordError, setPasswordError] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);
  // Remove unused 'today' variable
  // let today = new Date(); 
  const [password, setPassword] = useState("");
  const userImageUrls = invite?.roomUserImages ?? room?.users.map(u => u.photoUrl ?? Util.ANONYMOUS_PHOTO_URL) ?? [];

  useEffect(() => {
    const platform = UtilApp.getPlatform();
    setIsMobileBrowser(platform === 'android-browser' || platform === 'ios-browser');
    start();
  }, [invitationId])

  async function start() {
    setInProgress(true);
    let resp = await Fire.item.getRoomInviteInfo(invitationId || "");
    console.log("Room invite response:", resp);
    if (resp?.status === 'EXPIRED') {
      setInProgress(false);
      setIsExpired(true);
      return;
    }
    if (resp && resp.link) {
      setInvite(resp.link);
      const inv = resp.link;
      if (inv && My.isLoggedIn()) {
        setRoom(await Fire.getRoom(inv.roomId));
      }
      setInProgress(false);
    }
  }

  async function joinRoom() {
    if (room && My.isLoggedIn()) {
      if (invite?.password && invite.password !== password) {
        setPasswordError(t`Password is incorrect.`);
        return;
      }

      setInProgress(true);
      await Fire.joinRoom(room.id, My.user, invite?.creator);
      setInProgress(false);
      history.replace(`/room/${room.id}`);
    }
  }

  return (
    <div className="h-full bg-gray-50 p-4 flex flex-col items-center justify-center">

      {/* Header for Back Navigation */}
      {My.isLoggedIn() && (
        <div className="absolute top-4 left-4">
          <Button variant="ghost" size="icon" onClick={() => history.goBack()}>
            <ArrowLeft className="w-6 h-6" />
          </Button>
        </div>
      )}

      {/* Loading State */}
      {!invite && inProgress && (
        <div className="text-center mt-20">
          <p className="text-gray-500 mb-4"><Trans>Fetching the invitation...</Trans></p>
          <Player autoplay keepLastFrame src="assets/splash.json" style={{ height: '160px', width: '160px' }} />
        </div>
      )}

      {/* Invitation Content */}
      {invite && (
        <Card className="w-full max-w-md mx-auto shadow-lg border-none">
          <CardHeader className="text-center pb-2">
            <p className="text-lg font-medium text-gray-900"><Trans>Invitation link by {invite.creator.name}</Trans></p>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4 pt-4">
            {roomImage && (
              <img src={roomImage} alt="Room" className="w-24 h-24 rounded-2xl object-cover shadow-sm" />
            )}

            <div className="text-center space-y-2">
              {roomName && <h2 className="text-2xl font-bold text-gray-900"># {roomName}</h2>}
              {roomDescription && <p className="text-gray-600">{roomDescription}</p>}
            </div>

            {getMemberList(6, userImageUrls)}

            <div className="w-full pt-4">
              {getActionButton()}
            </div>

            {getPromotionText()}
          </CardContent>
        </Card>
      )}

      {/* Error/Expired States */}
      {(!inProgress && !invite) && (
        <div className="text-center space-y-6 max-w-sm mx-auto mt-12 bg-white p-8 rounded-xl shadow-sm">
          {isExpired ? (
            <div className="flex flex-col items-center gap-4">
              <XCircle className="w-16 h-16 text-red-500" />
              <h1 className="text-xl font-bold text-gray-900"><Trans>Link Expired</Trans></h1>
              <p className="text-gray-600"><Trans>This invitation link has expired.</Trans></p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="w-16 h-16 text-gray-400" />
              <h4 className="text-xl font-medium text-gray-900"><Trans>Invitation cannot be found.</Trans></h4>
              <Button variant="outline" onClick={() => history.push('/')}>
                <Trans>Back</Trans>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  function getMemberList(max: number, userImageUrls: string[]) {
    if (userImageUrls.length === 0) return null;

    return (
      <div className="bg-gray-50 rounded-xl p-4 w-full text-center">
        <div className="flex flex-wrap justify-center gap-2 mb-2">
          {userImageUrls.slice(0, 10).map((photoUrl, idx) => (
            <Avatar key={`${photoUrl}-${idx}`} className="w-8 h-8 ring-2 ring-white">
              <ProfileImg url={photoUrl} className="" />
            </Avatar>
          ))}
        </div>
        <p className="text-sm text-gray-500 font-medium"><Trans>{userImageUrls.length} members</Trans></p>
      </div>
    );
  }

  function getActionButton() {
    if (My.isLoggedIn())
      return getJoinButton();
    else {
      if (isMobileBrowser) {
        let openWithAppLink = `https://sori.page?link=https://app.nextpod.jp/invitation/${invitationId}&apn=com.jiran.sori&ibi=com.jiran.sori&isi=6444708635`;

        if (UtilApp.getPlatform() === 'ios-browser') {
          // TODO: Do this for Android later when Android users have updated their clients.
          openWithAppLink = `sorichat://app.nextpod.jp/invitation/${invitationId}`;
        }
        return (
          <div className="text-center space-y-4 p-4 bg-blue-50 rounded-xl">
            <h6 className="font-semibold text-blue-900"><Trans>You're on a mobile browser.</Trans></h6>
            <div className="space-y-2">
              <p className="text-sm text-blue-800"><Trans>Install the mobile app</Trans></p>
              <div className="flex justify-center gap-2">
                <a href="https://play.google.com/store/apps/details?id=com.jiran.sori" target="_blank" rel="noopener noreferrer">
                  <img src="/assets/img/badge-google.png" className="h-9" alt="Get it on Google Play" />
                </a>
                <a href="https://apps.apple.com/app/sorichat/id6444708635" target="_blank" rel="noopener noreferrer">
                  <img src="/assets/img/badge-apple.png" className="h-9" alt="Download on the App Store" />
                </a>
              </div>
            </div>

            <div className="pt-2">
              <p className="text-sm text-blue-800 mb-2"><Trans>Already installed?</Trans></p>
              <a href={openWithAppLink} className="inline-block px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700 transition-colors">
                <Trans>Open with NEXTPOD app</Trans>
              </a>
            </div>

            <p className="text-xs text-blue-600 pt-2"><Trans>You can also use NEXTPOD on desktop at https://app.nextpod.jp</Trans></p>
          </div>
        );
      } else {
        return getLoginButton()
      }
    }
  }

  function getJoinButton() {
    if (room) {
      if (room.userIds.includes(My.user.id)) {
        return (
          <div className="space-y-3 w-full">
            <p className="text-sm text-center text-gray-600"><Trans>You have already joined #{`${room.name}`}.</Trans></p>
            <Button onClick={() => history.replace(`/room/${room.id}`)} className="w-full">
              <Trans>Go to room</Trans>
            </Button>
          </div>
        );
      } else {
        return (
          <div className="space-y-3 w-full">
            {invite?.password && (
              <div className="space-y-1">
                <Input
                  type="password"
                  placeholder={t`Enter room password.`}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError('');
                  }}
                  className={passwordError ? "border-red-500 focus-visible:ring-red-500" : ""}
                />
                {passwordError && <p className="text-xs text-red-500 ml-1">{passwordError}</p>}
              </div>
            )}
            <Button disabled={inProgress} onClick={joinRoom} data-cy="join" className="w-full bg-orange-500 hover:bg-orange-600 text-white">
              <Trans>Join the room</Trans>
            </Button>
          </div>
        );
      }
    }
    return null;
  }

  function getLoginButton() {
    let path = `/invitation/${invitationId}`;
    return (
      <div className="space-y-3 w-full">
        <p className="text-sm text-center text-gray-600"><Trans>You need to sign up/login to join the room.</Trans></p>
        <Button
          onClick={() => history.push(`/login?redirect=${path}`)}
          data-cy="signup"
          variant="outline"
          className="w-full"
        >
          <Trans>Sign up/login</Trans>
        </Button>
      </div>
    );
  }

  function getPromotionText() {
    if (!My.isLoggedIn()) {
      return (
        <div className="w-full">
          <Separator className="my-6" />
          <div className="text-center space-y-3">
            <div className="flex justify-center">
              <img src="assets/icon/favicon.png" alt="Logo" className="w-10 h-10 rounded-lg shadow-sm" />
            </div>
            <h5 className="font-semibold text-gray-900 leading-tight">
              <Trans>Easy communication with NEXTPOD<br /> No matter where you are.</Trans>
            </h5>
            <p className="text-sm text-gray-600 leading-relaxed text-justify px-2">
              <Trans>NEXTPOD is a cloud based communication service that is designed to simplify remote collaboration between
                teams across and within organizations. You can create private rooms and invite people for collaboration
                using instant messaging, voice and screen recordings, file sharing and real time voice calls.</Trans>
            </p>
          </div>
        </div>
      );
    }
    return null; // Return null if logged in
  }
}

export default RoomInvitationPage;